import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnInit} from '@angular/core';
import {FormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {of} from 'rxjs';
import {catchError, finalize} from 'rxjs/operators';
import {MaterialModule} from '../../../material.module';
import {ClientService} from '../../../services/auth/client.service';
import {
    ShineAuthDomainsService,
    ShineDomainRecord
} from '../../../services/auth/shine-auth/shine-auth-domains.service';
import {ToastService} from '../../../services/layout/toast.service';
import {ConfirmDialogComponent} from '../../confirm-dialog/confirm-dialog.component';

type DomainStatus = 'pending' | 'verified' | 'failed' | 'inactive';
type BadgeResult = {label: string; className: string};

@Component({
    selector: 'app-domain-settings',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MaterialModule],
    templateUrl: './domain-settings.component.html',
    styleUrls: ['../../connect/connect-shared.scss', './domain-settings.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {class: 'connect-theme'}
})
export class DomainSettingsComponent implements OnInit {
    private readonly domainsService = inject(ShineAuthDomainsService);
    private readonly clientService = inject(ClientService);
    private readonly toastService = inject(ToastService);
    private readonly dialog = inject(MatDialog);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly fb = inject(FormBuilder);

    domains: ShineDomainRecord[] = [];
    loading = false;
    showCreate = false;
    createBusy = false;
    error: string | null = null;
    savingIds = new Set<string>();

    readonly createForm = this.fb.group({
        domain: ['', Validators.required],
        status: this.fb.control<DomainStatus>('verified', {nonNullable: true}),
    });

    ngOnInit(): void {
        this.loadDomains();
    }

    toggleCreate(): void {
        this.showCreate = !this.showCreate;
        this.cdr.markForCheck();
    }

    loadDomains(): void {
        const clientId = this.clientService.client?.id;
        if (!clientId) {
            this.error = 'Select a client to manage domains.';
            this.domains = [];
            this.cdr.markForCheck();
            return;
        }

        this.loading = true;
        this.error = null;
        this.domainsService
            .listDomains(clientId)
            .pipe(
                catchError(err => {
                    console.error('Failed to load domains', err);
                    this.error = 'Could not load domains yet.';
                    this.toastService.showError(this.error);
                    this.domains = [];
                    this.cdr.markForCheck();
                    return of<ShineDomainRecord[]>([]);
                }),
                finalize(() => {
                    this.loading = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe(domains => {
                this.domains = domains;
                this.cdr.markForCheck();
            });
    }

    createDomain(): void {
        if (this.createForm.invalid) {
            this.toastService.showError('Enter a valid domain.');
            return;
        }

        const clientId = this.clientService.client?.id;
        if (!clientId) {
            this.toastService.showError('Select a client to add domains.');
            return;
        }

        const normalized = this.normalizeDomain(this.createForm.value.domain);
        if (!normalized) {
            this.toastService.showError('Enter a valid domain.');
            return;
        }

        this.createBusy = true;
        const status = this.createForm.value.status || 'verified';
        this.domainsService
            .createDomain(clientId, {domain: normalized, status})
            .pipe(
                finalize(() => {
                    this.createBusy = false;
                    this.cdr.markForCheck();
                })
            )
            .subscribe(record => {
                if (!record) {
                    this.toastService.showError('Could not add domain.');
                    return;
                }
                this.toastService.showSuccess('Domain added.');
                this.createForm.reset({domain: '', status: 'verified'});
                this.showCreate = false;
                this.loadDomains();
            });
    }

    toggleStatus(domain: ShineDomainRecord): void {
        const clientId = this.clientService.client?.id;
        if (!clientId) return;
        const nextStatus: DomainStatus =
            domain.status === 'verified' ? 'inactive' : 'verified';
        this.updateDomain(domain, {status: nextStatus});
    }

    deleteDomain(domain: ShineDomainRecord): void {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            width: '350px',
            data: {
                title: 'Delete domain?',
                message: `Delete ${domain.domain}?`,
                info: 'This removes it from the allowlist.',
                confirmButtonText: 'Delete',
                cancelButtonText: 'Cancel'
            }
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) return;
            const clientId = this.clientService.client?.id;
            if (!clientId) return;
            this.savingIds.add(domain.id);
            this.domainsService
                .deleteDomain(clientId, domain.id)
                .pipe(
                    finalize(() => {
                        this.savingIds.delete(domain.id);
                        this.cdr.markForCheck();
                    })
                )
                .subscribe(ok => {
                    if (!ok) {
                        this.toastService.showError('Could not delete domain.');
                        return;
                    }
                    this.toastService.showSuccess('Domain deleted.');
                    this.loadDomains();
                });
        });
    }

    isSaving(id: string): boolean {
        return this.savingIds.has(id);
    }

    statusBadge(status: string | null | undefined): BadgeResult {
        const normalized = (status || '').toLowerCase();
        switch (normalized) {
            case 'verified':
                return {label: 'Verified', className: 'badge green'};
            case 'pending':
                return {label: 'Pending', className: 'badge amber'};
            case 'failed':
                return {label: 'Failed', className: 'badge red'};
            case 'inactive':
                return {label: 'Inactive', className: 'badge gray'};
            default:
                return {label: 'Unknown', className: 'badge gray'};
        }
    }

    private updateDomain(domain: ShineDomainRecord, payload: {status?: DomainStatus; domain?: string}): void {
        const clientId = this.clientService.client?.id;
        if (!clientId) return;
        this.savingIds.add(domain.id);
        this.domainsService
            .updateDomain(clientId, domain.id, payload)
            .pipe(
                finalize(() => {
                    this.savingIds.delete(domain.id);
                    this.cdr.markForCheck();
                })
            )
            .subscribe(record => {
                if (!record) {
                    this.toastService.showError('Could not update domain.');
                    return;
                }
                this.toastService.showSuccess('Domain updated.');
                this.loadDomains();
            });
    }

    private normalizeDomain(value: string | null | undefined): string | null {
        if (!value) return null;
        let trimmed = value.trim().toLowerCase();
        if (!trimmed) return null;
        trimmed = trimmed.replace(/^\*\./, '');

        const candidate = trimmed.includes('://') ? trimmed : `https://${trimmed}`;
        try {
            const url = new URL(candidate);
            trimmed = url.hostname;
        } catch {
            // fall back to basic trimming
            const slashIndex = trimmed.indexOf('/');
            if (slashIndex >= 0) trimmed = trimmed.slice(0, slashIndex);
            const atIndex = trimmed.lastIndexOf('@');
            if (atIndex >= 0) trimmed = trimmed.slice(atIndex + 1);
            const colonIndex = trimmed.indexOf(':');
            if (colonIndex >= 0) trimmed = trimmed.slice(0, colonIndex);
        }

        trimmed = trimmed.replace(/^www\./, '');
        trimmed = trimmed.replace(/\.+$/, '');
        return trimmed || null;
    }
}
